import { Component, Input } from '@angular/core';
import Chart from 'chart.js/auto';
import { IFilterOptions, IItem, defaultFilterOptions } from '../health-ministry-dashboard.component';
import { AppService } from '../../../services/app.service';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Tool } from '@bryntum/schedulerpro';

interface IFilterValues {
    dateFrom?: Date;
    dateTo?: Date;
    status?: string;
    speciality?: string;
    workplace?: string;
    clinicalBase?: string;
}

interface IChart {
    id: string;
    title: string;
    data: IChartData[];
    chart?: any;
}

interface IChartData {
    label: string;
    value: number;
    percents: string;
}

const colors = [
    'rgba(240, 50, 50 , .75)',
    'rgba(255, 180, 50, .75)',
    'rgba(255, 220, 90, .75)',
    'rgba(100, 210, 80, .75)',
    'rgba(90, 165, 255, .75)',
    'rgba(170, 90, 240, .75)',
    'rgba(180, 180, 180, .75)',
    'rgba(110, 110, 110, .75)',
    'rgba(170, 150, 110, .75)',
    'rgba(130, 50, 20 , .75)'
];

const cache: {
    filters?: IFilterValues
} = {};

const grouplimit = 10;

function clearCache() {
    cache.filters = undefined;
}

let isRouterSubscribed = false;

@Component({
    selector: 'app-health-ministry-dashboard-stats',
    templateUrl: './health-ministry-dashboard-stats.component.html',
    styleUrls: ['./health-ministry-dashboard-stats.component.scss']
})
export class HealthMinistryDashboardStatsComponent {
    constructor(
        private readonly app: AppService,
        private readonly router: Router
    ) { }

    @Input() data: IItem[] = [];
    @Input() filters: IFilterOptions = defaultFilterOptions;

    readonly colors = colors;

    charts: IChart[] = [];

    totals: {
        label: string,
        value: number
    }[] = [];

    filterValues: IFilterValues = {};

    private prevFilter: string;

    ngOnInit() {
        if (!isRouterSubscribed) {
            isRouterSubscribed = true;
            this.router.events.subscribe((e: RouterEvent) => {
                if (e.url && e instanceof NavigationStart && !e.url.toLowerCase().includes('/health-ministry-dashboard')) {
                    clearCache();
                }
            });
        }

        this.filterValues = cache.filters || {};
        this.filter();
    }

    filter() {
        cache.filters = this.filterValues;

        const from = this.filterValues.dateFrom;
        const to = this.filterValues.dateTo;
        const status = this.filterValues.status;
        const spec = this.filterValues.speciality;
        const workplace = this.filterValues.workplace;
        const base = this.filterValues.clinicalBase;

        const next = [
            from,
            to,
            status,
            spec,
            workplace,
            base
        ].join(';;');

        if (next == this.prevFilter) {
            return;
        }

        this.prevFilter = next;

        if (from || to || status || spec || workplace || base) {
            const filtered = this.data.filter(t => {
                if (from && t.dateFrom < from) return false;
                if (to && t.dateTo > to) return false;
                if (status && t.StudentStatusName != status) return false;
                if (spec && t.StudyProgramName != spec) return false;
                if (workplace && t.WorkplaceName != workplace) return false;
                if (base && t.ClinicalBaseName != base) return false;
                return true;
            });

            this.buildStats(filtered);
        } else {
            this.buildStats(this.data);
        }
    }

    hasLongLabels(data: { label: string }[]): boolean {
        return data.some(t => t.label?.length > 50);
    }

    getPercents(value: number, total: number): string {
        const percents = value / total * 100;
        return percents.toFixed(2);
    }

    private dataToArray(data: { [label: string]: number }): IChartData[] {
        const arr: { label: string, value: number, percents: string }[] = [];

        const total = Object.values(data).reduce((t, n) => {
            t += n;
            return t;
        }, 0);

        Object.keys(data).forEach(t => {
            arr.push({
                label: t,
                value: data[t],
                percents: this.getPercents(data[t], total)
            });
        });

        arr.sort((a, b) => {
            return b.value - a.value;
        });

        return arr;
    }

    private buildPieChart(id: string, data: IChartData[]) {
        const pie = new Chart(id, {
            type: 'pie',
            data: {
                labels: data.map(t => t.label),
                datasets: [
                    {
                        data: data.map(t => t.value),
                        backgroundColor: colors
                    }
                ]
            },
            options: {
                responsive: true,
                aspectRatio: 2,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        enabled: false,
                        position: 'nearest',
                        external: context => {
                            const { chart, tooltip } = context;
                            const elId = `${id}-tooltip`;

                            let el = document.getElementById(elId);

                            if (!el) {
                                el = document.createElement('div');
                                el.id = elId;
                                el.style.background = 'rgba(0, 0, 0, 0.8)';
                                el.style.borderRadius = '4px';
                                el.style.color = 'white';
                                el.style.lineHeight = '1.2';
                                el.style.fontSize = '13px';
                                el.style.padding = '8px 12px';
                                el.style.opacity = '1';
                                el.style.pointerEvents = 'none';
                                el.style.position = 'absolute';
                                el.style.transform = 'translate(-50%, 0)';
                                el.style.transition = 'all .1s ease';
                                document.getElementById(`chart-${id}`).appendChild(el);
                            }

                            // hide if no tooltip
                            if (tooltip.opacity === 0) {
                                el.style.opacity = '0';
                                return;
                            }

                            // set text
                            if (tooltip.body) {
                                const tooltipData = tooltip.dataPoints[0];

                                const titleLine = tooltipData.label;
                                const bodyLine = `${tooltipData.parsed} (${data[tooltipData.dataIndex].percents}%)`;

                                el.innerHTML = `<div style='font-weight: 500;'>${titleLine}</div>
                                <div style='margin-top: 8px'>${bodyLine}</div>`;
                            }

                            const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

                            // display, position, and set styles for font
                            el.style.opacity = '1';
                            el.style.left = positionX + tooltip.caretX + 'px';
                            el.style.top = positionY + tooltip.caretY + 'px';
                        }
                    }
                }
            }
        });

        return pie;
    }

    private buildStats(data: IItem[]) {
        const byYear: { [year: string]: number } = {};
        const byStatus: { [status: string]: number } = {};
        const byStudyType: { [studyType: string]: number } = {};
        const bySpec: { [spec: string]: number } = {};
        const byWorkplace: { [workplace: string]: number } = {};
        const byClinicalBase: { [clinicalBase: string]: number } = {};
        const bySpecFirstYear: { [spec: string]: number } = {};
        const bySpecExmatriculated: { [spec: string]: number } = {};
        const bySpecExpelled: { [spec: string]: number } = {};

        const add = (group: { [key: string]: number }, key: string) => {
            if (key === undefined || key === null) {
                key = this.app.translate('healthMinistryChart_none');
            }

            if (!group[key]) {
                group[key] = 0;
            }

            group[key]++;
        };

        const activeSpecs: string[] = [];
        const totals = {
            residents: 0,
            academicVacation: 0,
            active: 0,
            activeSpecs: 0
        };

        totals.residents = data.length;

        const yearLabel = this.app.translate('healthMinistryChart_yearLabel');

        data.forEach(t => {
            if (t.StudentStatusId == 1) {
                totals.active++;

                if (!activeSpecs.includes(t.StudyProgramName)) {
                    activeSpecs.push(t.StudyProgramName);
                    totals.activeSpecs++;
                }
            }

            if (t.StudentStatusId == 2) {
                totals.academicVacation++;
            }

            add(byYear, yearLabel.replace('{{year}}', t.StudyYear));
            add(byStatus, t.StudentStatusName);
            add(byStudyType, t.StudyTypeName);
            add(bySpec, t.StudyProgramName);
            add(byWorkplace, t.WorkplaceName);
            add(byClinicalBase, t.ClinicalBaseName);

            if (t.StudyYear == 1) {
                add(bySpecFirstYear, t.StudyProgramName);
            }

            if (t.StudentStatusId == 3) {
                add(bySpecExpelled, t.StudyProgramName);
            }

            if (t.StudentStatusId == 4) {
                add(bySpecExmatriculated, t.StudyProgramName);
            }
        });

        this.charts = [];

        this.charts.push({
            id: 'chart-residents-by-year',
            title: this.app.translate('healthMinistryChart_residentsByYear'),
            data: this.dataToArray(byYear)
        });

        this.charts.push({
            id: 'chart-residents-by-status',
            title: this.app.translate('healthMinistryChart_residentsByStatus'),
            data: this.dataToArray(byStatus)
        });

        this.charts.push({
            id: 'chart-residents-by-financing',
            title: this.app.translate('healthMinistryChart_residentsByFinancing'),
            data: this.dataToArray(byStudyType)
        });

        this.charts.push({
            id: 'chart-residents-by-spec',
            title: this.app.translate('healthMinistryChart_residentsBySpeciality'),
            data: this.dataToArray(bySpec)
        });

        this.charts.push({
            id: 'chart-residents-by-workplace',
            title: this.app.translate('healthMinistryChart_residentsByWorkplace'),
            data: this.dataToArray(byWorkplace)
        });

        this.charts.push({
            id: 'chart-residents-by-clinical-base',
            title: this.app.translate('healthMinistryChart_residentsByClinicalBase'),
            data: this.dataToArray(byClinicalBase)
        });

        this.charts.push({
            id: 'chart-residents-by-spec-first-year',
            title: this.app.translate('healthMinistryChart_residentsBySpecialityFirstYear'),
            data: this.dataToArray(bySpecFirstYear)
        });

        this.charts.push({
            id: 'chart-residents-by-spec-expelled',
            title: this.app.translate('healthMinistryChart_residentsBySpecialityExpelled'),
            data: this.dataToArray(bySpecExpelled)
        });

        this.charts.push({
            id: 'chart-residents-by-spec-exmatriculated',
            title: this.app.translate('healthMinistryChart_residentsBySpecialityExmatriculated'),
            data: this.dataToArray(bySpecExmatriculated)
        });

        this.totals = [
            {
                label: this.app.translate('healthMinistryDashboard_totalResidents'),
                value: totals.residents
            },
            {
                label: this.app.translate('healthMinistryDashboard_totalActive'),
                value: totals.active
            },
            {
                label: this.app.translate('healthMinistryDashboard_totalAcademicVacation'),
                value: totals.academicVacation
            },
            {
                label: this.app.translate('healthMinistryDashboard_totalActiveSpecialities'),
                value: totals.activeSpecs
            }
        ];

        // wait for the DOM
        setTimeout(() => {
            this.charts.forEach(t => {
                if (t.data.length > grouplimit) {
                    t.data = t.data.slice(0, grouplimit);
                    t.title += ` (top ${grouplimit})`;
                }

                t.chart = this.buildPieChart(t.id, t.data);
            });
        }, 500);
    }
}
